import { Loader2, Plus } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import type { ReorderableItem } from "./PreviouslyOrderedSection";

export const PreviouslyOrderedCard: React.FC<{
  item: ReorderableItem;
  isAdding: boolean;
  onReorder: (item: ReorderableItem, info: { imageUrl: string; rect: DOMRect }) => void;
}> = ({ item, isAdding, onReorder }) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const image = item.product.images[0];

  const handleClick = () => {
    if (isAdding || !image) return;
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const imageUrl = image.url.startsWith("https://cdn")
      ? image.url
      : `/image/${image.id}?size=400&q=75`;
    onReorder(item, { imageUrl, rect });
  };

  const labels = item.selectionSummaryLabels;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isAdding}
      className="w-full h-[300px] flex flex-col text-left rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-brand-mint/40 transition-all cursor-pointer disabled:opacity-60 disabled:cursor-wait overflow-hidden"
    >
      {/* Image */}
      <div ref={imageRef} className="relative h-[160px] shrink-0 bg-gray-50">
        {image && (
          <ResponsiveImage
            image={image}
            objectFit="cover"
            forceSize={400}
            lazy
            className="w-full h-full"
          />
        )}
        {/* Quick-add badge */}
        <div className="absolute top-2 right-2 flex items-center justify-center size-8 rounded-full bg-brand-mint text-brand-black shadow-sm">
          {isAdding ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" strokeWidth={2.5} />
          )}
        </div>
      </div>

      {/* Info — fixed remaining space, overflow hidden */}
      <div className="p-3 flex-1 overflow-hidden">
        <p className="text-sm font-semibold text-brand-black leading-snug line-clamp-1">
          {item.product.name}
        </p>
        {labels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {labels.map((label) => (
              <span
                key={label}
                className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600 font-medium leading-snug"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};
